import axios from "axios";

/**
 * CSRF double-submit cookie helpers.
 *
 * The backend issues a non-HttpOnly cookie named ``payassist_csrf`` alongside
 * the encrypted session cookie. On every state-changing request the SPA must
 * echo that value back in the ``X-CSRF-Token`` header. The server compares it
 * to the ``csrfToken`` field stored inside the encrypted session, which an
 * attacker cannot read.
 */
export const CSRF_COOKIE_NAME = "payassist_csrf";
export const CSRF_HEADER_NAME = "X-CSRF-Token";

/** Read the CSRF cookie value (returns empty string if missing). */
export function getCsrfToken(): string {
  const prefix = `${CSRF_COOKIE_NAME}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return "";
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * Install a global axios interceptor that attaches ``X-CSRF-Token`` to every
 * unsafe API request. Idempotent - safe to call multiple times.
 */
let installed = false;
export function installCsrfInterceptor(): void {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const method = (config.method || "get").toUpperCase();
    if (!UNSAFE_METHODS.has(method)) return config;

    const token = getCsrfToken();
    if (token) {
      config.headers = config.headers ?? {};
      // axios v1 uses AxiosHeaders; both shapes support bracket assignment.
      (config.headers as Record<string, string>)[CSRF_HEADER_NAME] = token;
    }
    return config;
  });
}
